import { useState } from 'react';
import { Loader2Icon, PlusIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useCreateBackOfficeSample } from '@/services/projectBackOffice.service';
import ProjectSelect from './ProjectSelect';
import { saveErrorMessage } from './saveError';

const FIELD =
    'bg-[#0f141a] border-[#1e242a] text-[#e5e7e3] placeholder:text-[#6b7480]';
const LABEL =
    'block text-[11px] uppercase tracking-wider text-[#6b7480] mb-1.5';

/** A name, a description if any, and a project: a sample no scanner feeds
 *  until a hub ties one to it. */
export default function AddSampleDialog() {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button
                size="sm"
                onClick={() => setOpen(true)}
                className="bg-[#2f6f4e] hover:bg-[#3a865f] text-white"
            >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Add sample
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md bg-[#161c22] border-[#1e242a] text-[#d8dde2]">
                    <DialogHeader>
                        <DialogTitle className="text-white">
                            Add sample
                        </DialogTitle>
                    </DialogHeader>
                    {/* Remounts each time, so the last entry is gone. */}
                    {open && <AddSampleForm onDone={() => setOpen(false)} />}
                </DialogContent>
            </Dialog>
        </>
    );
}

function AddSampleForm({ onDone }: { onDone: () => void }) {
    const create = useCreateBackOfficeSample();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [projectId, setProjectId] = useState('');

    const canSave = !!name.trim() && !!projectId && !create.isPending;

    const save = () => {
        if (!canSave) return;
        create.mutate(
            {
                projectId,
                name: name.trim(),
                description: description.trim() || undefined,
            },
            { onSuccess: onDone },
        );
    };
    const onEnter = (e: React.KeyboardEvent) => e.key === 'Enter' && save();

    return (
        <div className="space-y-4">
            <div>
                <label className={LABEL}>Name</label>
                <Input
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={onEnter}
                    className={FIELD}
                />
            </div>
            <div>
                <label className={LABEL}>Description</label>
                <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onKeyDown={onEnter}
                    placeholder="Optional"
                    className={FIELD}
                />
            </div>
            <div>
                <label className={LABEL}>Project</label>
                <ProjectSelect value={projectId} onChange={setProjectId} />
            </div>
            {create.error && (
                <p className="text-sm text-red-400">
                    {saveErrorMessage(create.error)}
                </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
                <Button
                    variant="ghost"
                    onClick={onDone}
                    className="text-[#a8b0bb] hover:bg-[#1e242a] hover:text-white"
                >
                    Cancel
                </Button>
                <Button
                    onClick={save}
                    disabled={!canSave}
                    className="bg-[#2f6f4e] hover:bg-[#3a865f] text-white"
                >
                    {create.isPending && (
                        <Loader2Icon className="h-4 w-4 mr-1.5 animate-spin" />
                    )}
                    Save
                </Button>
            </div>
        </div>
    );
}
